import React, { ChangeEvent, useContext, useState, useEffect, useRef } from 'react';
import {
  Card, CardContent, CardHeader, Divider, Grid, Table, Tabs, Tab, Select, FormControl,
  TableBody, TableCell, TableHead, TableRow, TextField, Icon, TablePagination, CircularProgress, Box,
  MenuItem,
  InputLabel
} from '@material-ui/core';
import { LanguageString } from '../Common';
import { useJobs, useTraining, useTrainingFilters } from '../../hooks';
import { StudentContext, SiteContext } from '../../contexts';

const JobLink: React.FC<{ url: string, children: React.ReactNode }> = ({ url, children }) => {
  return <a href={url} target="_blank" rel="noreferrer" style={{textDecoration: 'none', color: 'inherit'}}>{children}</a>;
};

interface filterOptions {
  id: number
  text: string
  value: string
};

export const CareerOneStop: React.FC = () => {

  const rowsPerPageOptions = [10, 25, 50];
  const [page, setPage] = useState(0);
  const [trainingPage, setTrainingPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);

  const [value, setValue] = useState<number>(1);  const [keyword, setKeyword] = useState<string>('');
  const [trainingKeyword, setTrainingKeyword] = useState<string>('');
  const [days, setDays] = useState<number>(30);
  const [radius, setRadius] = useState<number>(25);
  const [zip, setZip] = useState<string>('');
  const [trainingZip, setTrainingZip] = useState<string>('');
  const [occupation, setOccupation] = useState<string>('0');
  const [programName, setProgramName] = useState<string>('0');
  const [programLength, setProgramLength] = useState<string>('0');
  const [occupationOptions, setOccupationOptions] = useState<filterOptions[]>([]);
  const [programNameOptions, setProgramNameOptions] = useState<filterOptions[]>([]);
  const [programLengthOptions, setProgramLengthOptions] = useState<filterOptions[]>([]);
  const initialDataLoaded = useRef(false);

  const studentCtx = useContext(StudentContext);
  const siteCtx = useContext(SiteContext);
  const { loading, data } = useJobs({
    variables: {
      userId: studentCtx?.student.id ?? 0,
      offset: page * rowsPerPage,
      pageSize: rowsPerPage,
      days: days,
      radius: radius,
      keyword: keyword,
      zip: zip?.toString()
    }
   });

  const { loading: filterLoading, data: filterData } = useTrainingFilters({
    variables: {
      userId: studentCtx?.student.id ?? 0,
      zip: trainingZip?.toString()
    }
  });

  const { loading: trainingLoading, data: trainingData } = useTraining({
    variables: {
      userId: studentCtx?.student.id ?? 0,
      offset: trainingPage * rowsPerPage,
      pageSize: rowsPerPage,
      days: days,
      radius: radius,
      occupation: occupation !== '' ? occupation : '0',
      programName: programName !== '' ? programName : '0',
      programLength: programLength !== '' ? programLength : '0',
      keyword: trainingKeyword,
      zip: trainingZip?.toString()
    }
  });

  useEffect(() => {
    if (filterData && filterLoading === false && !initialDataLoaded.current) {
      const initialOccupations = [...(filterData.user.trainingFilters.occupationList.filterList ?? [])]
        ?.sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''))
        ?.map((filter, index) => {
          return {
            id: index,
            text: filter.value ?? '',
            value: filter.value ?? ''
          };
        });
      const initialProgramNames = [...(filterData.user.trainingFilters.programCountList.filterList ?? [])]
        ?.sort((a, b) => (a.value ?? '').localeCompare(b.value ?? ''))
        ?.map((filter, index) => {
          return {
            id: index,
            text: filter.value ?? '',
            value: filter.value ?? ''
          };
        });
      const initialProgramLengths = [
        {
          id: 0,
          text: 'Certificates of less than 12 weeks',
          value: 'Certificates of less than 12 weeks'
        },
        {
          id: 1,
          text: 'Certificates of at least 12 weeks but less than 1 year',
          value: 'Certificates of at least 12 weeks but less than 1 year'
        },
        {
          id: 2,
          text: 'Less than one year',
          value: 'Less than one year'
        },
        {
          id: 3,
          text: 'At least one but less than two years',
          value: 'At least one but less than two years'
        },
        {
          id: 4,
          text: '2 years',
          value: '2 years'
        },
        {
          id: 5,
          text: 'At least two but less than four years',
          value: 'At least two but less than four years'
        },
        {
          id: 6,
          text: '4 years',
          value: '4 years'
        },
        {
          id: 7,
          text: 'More than 4 years',
          value: 'More than 4 years'
        }
      ];

      setOccupationOptions(initialOccupations ?? []);
      setProgramLengthOptions(initialProgramLengths ?? []);
      setProgramNameOptions(initialProgramNames ?? []);
      initialDataLoaded.current = true;
    }
  }, [filterData, filterLoading]);

  const handleChange = (event: ChangeEvent<{}>, val: number) => {
    setValue(val);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={
            siteCtx.site?.careerTrainingEnabled ? (
              <Tabs value={value} onChange={handleChange}>
                <Tab value={1} label={
                  <LanguageString groupName="CAREER_ONE_STOP" resourceName="Title" defaultText="Jobs" />
                } />
                <Tab value={2} label={
                  <LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="Title" defaultText="Training Providers" />
                } />
              </Tabs>
            ) : (
              <LanguageString groupName="CAREER_ONE_STOP" resourceName="Title" defaultText="Career One Stop Jobs" />
            )
          } />
          {value === 1 && (
            <CardContent >
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP" resourceName="SEARCH_KEYWORD" defaultText="Search Keyword" />}
                    value={keyword}
                    onChange={e => setKeyword(e.currentTarget.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP" resourceName="SEARCH_RADIUS" defaultText="Search Radius (in miles)" />}
                    value={radius}
                    onChange={e => setRadius(Number(e.currentTarget.value))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP" resourceName="SEARCH_AGE" defaultText="Job Age (in days)" />}
                    value={days}
                    onChange={e => setDays(Number(e.currentTarget.value))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP" resourceName="SEARCH_ZIP" defaultText="Job Location (zip code)" />}
                    value={zip}
                    onChange={e => setZip(e.currentTarget.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                {loading ? (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center">
                      <CircularProgress color="primary" />
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP" resourceName="JOB_TITLE" defaultText="Job Title" />
                          </TableCell>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP" resourceName="JOB_COMPANY" defaultText="Company" />
                          </TableCell>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP" resourceName="JOB_LOCATION" defaultText="Location" />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(data?.user.jobs.jobs ?? []).map(job => (
                          <TableRow key={`job_${job.id}`}>
                            <TableCell>
                              <JobLink url={job.url}>{job.jobTitle}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={job.url}>{job.company}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={job.url}>{job.location}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={job.url}>
                                <Icon color="primary">arrow_forward</Icon>
                              </JobLink>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center">
                    <TablePagination
                      rowsPerPageOptions={rowsPerPageOptions}
                      component="div"
                      count={data?.user.jobs.totalCount ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onChangePage={(_, p) => setPage(p)}
                      onChangeRowsPerPage={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          )}
          {value === 2 && (
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_KEYWORD" defaultText="Search Keyword" />}
                    value={trainingKeyword}
                    onChange={e => setTrainingKeyword(e.currentTarget.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_RADIUS" defaultText="Search Radius (in miles)" />}
                    value={radius}
                    onChange={e => setRadius(Number(e.currentTarget.value))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_ZIP" defaultText="Training Location (zip code)" />}
                    value={trainingZip}
                    onChange={e => setTrainingZip(e.currentTarget.value)}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id={`occupation_label`}>{<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_OCCUPATION" defaultText="Occupation" />}</InputLabel>
                    <Select
                      fullWidth
                      variant="outlined"
                      labelId='occupation_label'
                      label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_OCCUPATION" defaultText="Occupation" />}
                      value={occupation}
                      onChange={e => setOccupation(e.target.value as string)}
                    >
                      <MenuItem value=''>None</MenuItem>
                      {occupationOptions.map((fl, index) => {
                        return (
                          <MenuItem key={`occupation_${index}`} value={fl.value}>
                            {fl.value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id={`program_name_label`}>{<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_PROGRAM_NAME" defaultText="Program Name" />}</InputLabel>
                    <Select
                      fullWidth
                      variant="outlined"
                      labelId='program_name_label'
                      label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_PROGRAM_NAME" defaultText="Program Name" />}
                      value={programName}
                      onChange={e => setProgramName(e.target.value as string)}
                    >
                      <MenuItem value=''>None</MenuItem>
                      {programNameOptions.map((fl, index) => {
                        return (
                          <MenuItem key={`programName_${index}`} value={fl.value}>
                            {fl.value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel id={`program_length_label`}>{<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_PROGRAM_LENGTH" defaultText="Program Length" />}</InputLabel>
                    <Select
                      fullWidth
                      variant="outlined"
                      labelId='program_length_label'
                      label={<LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SEARCH_PROGRAM_LENGTH" defaultText="Program Length" />}
                      value={programLength}
                      onChange={e => setProgramLength(e.target.value as string)}
                    >
                      <MenuItem value=''>None</MenuItem>
                      {programLengthOptions.map((fl, index) => {
                        return (
                          <MenuItem key={`programLength_${index}`} value={fl.value}>
                            {fl.value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                {trainingLoading ? (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center">
                      <CircularProgress color="primary" />
                    </Box>
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="PROGRAM_TITLE" defaultText="Program Title" />
                          </TableCell>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SCHOOL_NAME" defaultText="School" />
                          </TableCell>
                          <TableCell>
                            <LanguageString groupName="CAREER_ONE_STOP_TRAINING" resourceName="SCHOOL_LOCATION" defaultText="Location" />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(trainingData?.user.training.training ?? []).map(training => (
                          <TableRow key={`job_${training.id}`}>
                            <TableCell>
                              <JobLink url={training.schoolUrl}>{training.programName}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={training.schoolUrl}>{training.schoolName}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={training.schoolUrl}>{training.city}, {training.stateAbbr}</JobLink>
                            </TableCell>
                            <TableCell>
                              <JobLink url={training.schoolUrl}>
                                <Icon color="primary">arrow_forward</Icon>
                              </JobLink>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center">
                    <TablePagination
                      rowsPerPageOptions={rowsPerPageOptions}
                      component="div"
                      count={trainingData?.user.training.totalCount ?? 0}
                      rowsPerPage={rowsPerPage}
                      page={trainingPage}
                      onChangePage={(_, p) => setTrainingPage(p)}
                      onChangeRowsPerPage={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setTrainingPage(0);
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          )}
        </Card>
      </Grid>
    </Grid>
  );
};
